import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Paper,
  CardHeader,
  Grid,
  Chip,
  DialogTitle,
  IconButton,
  Button
} from "@mui/material";
import {
  Close as CloseIcon,
  Attachment as AttachmentIcon,
  CheckCircleOutline
} from "@mui/icons-material";
import configAPI from "../../api/config";
import { UpdateStatusSchoolOnboardingData } from "../../api/auth/authService";
import { useSnackbar } from "../../components/SnackBar/SnackbarContext";

const SchoolOnboardingDetailsModal = ({
  open,
  onClose,
  school,
  isMobile,
  onRefresh 
}) => {
  const BASE_URL = configAPI.REACT_APP_API_BASE_URL;
  const { showSnackbar } = useSnackbar();

  if (!school) return null;

  const getFilesArray = (fileString) => {
  if (!fileString) return [];
  return fileString.split(",").map(f => f.trim()).filter(Boolean);
};

 const attachments = [
  { label: "School Sign", files: getFilesArray(school?.schoolSignUpload) },
  { label: "Teacher Document", files: getFilesArray(school?.teacherAttachFile) },
  { label: "Back Office Staff Document", files: getFilesArray(school?.totalBackOfficeAttachFile) },
  { label: "Fee Structure", files: getFilesArray(school?.feeStructureFile) },
  { label: "Class Structure", files: getFilesArray(school?.classStructureFile) },
  { label: "Student Admission Form", files: getFilesArray(school?.studentAdmissionFormFile) },
  { label: "Marksheet Sample", files: getFilesArray(school?.marksheetSampleFile) },
  { label: "Admit Card Sample", files: getFilesArray(school?.admitCardSampleFile) },
  { label: "Certificate Sample", files: getFilesArray(school?.certificateSampleFile) },
].filter(item => item.files.length > 0);

const openFile = (filePath) => {
  const cleanBase = BASE_URL.replace(/\/$/, "");
  const cleanPath = filePath.replace(/^\//, "");

  const fullUrl = `${cleanBase}/${cleanPath}`;
  window.open(fullUrl, "_blank");
};

// const openFile = (filePath) => {
//   const cleanBase = BASE_URL.replace(/\/$/, "");
//   const cleanPath = filePath.replace(/^\//, "");
//   const fullUrl = `${cleanBase}/${cleanPath}`;

//   const extension = cleanPath.split(".").pop()?.toLowerCase();

//   const openTypes = ["jpg", "jpeg", "png", "pdf"];
//   const downloadTypes = ["xls", "xlsx", "csv"];

//   if (openTypes.includes(extension)) {

//     window.open(fullUrl, "_blank");
//   } else if (downloadTypes.includes(extension)) {

//     const link = document.createElement("a");
//     link.href = fullUrl;
//     link.download = cleanPath.split("/").pop(); 
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   } else {
//     window.open(fullUrl, "_blank");
//   }
// };

const handleConfirmBoarding = async () => {
  try {
    const payload = {
      onboardingCode: school?.onboardingCode, 
      status: true
    };

    const response = await UpdateStatusSchoolOnboardingData(payload);

    if (response?.success === true) {
      showSnackbar(
        response?.message || "Status updated successfully",
        "success"
      );
      if (onRefresh) {
        await onRefresh();
      }

      onClose(); 
    } else {
      showSnackbar(
        response?.message || "Failed to update status",
        "error"
      );
    }

  } catch (error) {
    showSnackbar(
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong",
      "error"
    );
  }
};

  const SectionWrapper = ({ title, children }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "#fafafa",
        // width: "100%"
      }}
    >
      <CardHeader
        sx={{ px: 0, pb: 2 }}
        title={
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        }
      />
      {children}
    </Paper>
  );

  const Field = ({ label, value }) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || "—"}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "primary.main",
          color: "white"
        }}
      >
        <Typography variant="h6">
          {school?.schoolName || "School Details"}
        </Typography>

        <IconButton size="small" sx={{ color: "white" }} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
    <Box display="flex" flexDirection="column" gap={3}>

    {/*  SCHOOL DETAILS */}
    <SectionWrapper title="School Details">
      <Grid container spacing={1}>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="School Onboarding No." value={school?.onboardingCode} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="School Name" value={school?.schoolName} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="Website" value={school?.websiteUrl} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="Affiliated To" value={school?.schoolAffiliatedTo} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}} >
          <Field label="School Email" value={school?.schoolEmail} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}} >
          <Field label="School Phone Number" value={school?.schoolPhone} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}} >
          <Field label="School Registration Number" value={school?.schoolRegNo} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="Address" value={school?.address} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Field label="State" value={school?.state} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}} >
          <Field label="Country" value={school?.country} />
        </Grid>
      </Grid>
    </SectionWrapper>

    {/* DIRECTOR & ADMIN  */}
    <SectionWrapper title="Director & Admin Details">
      <Grid container spacing={1}>
        {/* Director Row */}
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Director Name" value={school?.directorName} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Director Contact" value={school?.directorContactNumber} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Director Email" value={school?.directorEmail} />
        </Grid>

        {/* Admin Row */}
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Admin Name" value={school?.adminName} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Admin Contact" value={school?.adminContactNumber} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4}}>
          <Field label="Admin Email" value={school?.adminEmail} />
        </Grid>
      </Grid>
    </SectionWrapper>

    {/* ACADEMIC & STAFF */}
    <SectionWrapper title="Academic & Staff Details">
      <Grid container spacing={1}>
        <Grid item size={{ xs: 12, md: 6}}>
          <Field label="Classes" value={school?.classes || school?.className} />
        </Grid>
        <Grid item size={{ xs: 12, md: 6}}>
          <Field label="Student Count" value={school?.studentCount} />
        </Grid>
        <Grid item size={{ xs: 12, md: 6}}>
          <Field label="Total Teachers" value={school?.totalNumberOfTeacher} />
        </Grid>
        <Grid item size={{ xs: 12, md: 6}}>
          <Field label="Back Office Staff" value={school?.totalBackOfficeStaff} />
        </Grid>
      </Grid>
    </SectionWrapper>

    {/*  ATTACHMENTS */}
    <SectionWrapper title="Attachments">
      <Box display="flex" flexDirection="column" gap={1.5}>
  {attachments.length > 0 ? (
    attachments.map((item, index) => (
      <Box key={index}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {item.label}
        </Typography>

        <Grid container spacing={1} mt={0.5}>
  {item.files.map((file, i) => (
    <Grid item xs={6} key={i}>
      <Chip
        icon={<AttachmentIcon fontSize="small" />}
        label={`File ${i + 1}`}
        onClick={() => openFile(file)}
        sx={{
          borderRadius: 2,
          cursor: "pointer",
          width: "100%",
          justifyContent: "flex-start"
        }}
      />
    </Grid>
  ))}
</Grid>
      </Box>
    ))
  ) : (
    <Typography variant="body2" color="text.secondary">
      No attachments available
    </Typography>
  )}
</Box>
     </SectionWrapper>
     <Box sx={{display: 'flex', justifyContent : 'flex-end' }}>
      <Button
        variant="contained"
        startIcon={<CheckCircleOutline />}
        onClick={handleConfirmBoarding}
       // onClick={handleAddAdminClick}
        sx={{
        alignSelf: { xs: "stretch", sm: "center", },
        "&:hover": { boxShadow: "none" },
         }}
         >
         Confirm Boarding
        </Button>
     </Box>

     </Box>
    </DialogContent>

    </Dialog>
  );
};

export default SchoolOnboardingDetailsModal;
